import { useCallback, useEffect, useRef, useState } from 'react'
import type { CatalogSearchService } from '../catalog/useCatalogSearch'
import { defaultCatalogSearchService } from '../catalog/useCatalogSearch'
import {
  AGENT_STEP_DELAYS_MS,
  AGENT_THINKING_STEPS,
  agentFollowUps,
  agentSummary,
  buildAgentShortlist,
} from './agentHeuristics'
import { getMockedAgentShortlist } from './mockedAgentResults'
import type { AgentAgentMessage, AgentMessage } from './types'

/** How many catalog rows to fetch per turn — enough headroom for
 * `buildAgentShortlist` to pick its top 3 from. */
const AGENT_SEARCH_LIMIT = 10

let nextMessageId = 0
/** Monotonic per-module id generator for thread messages (append-only
 * transcript, never reordered — a simple counter is a stable, testable key,
 * mirroring `MaterialsAiModal`'s session-scoped id precedent without
 * needing `crypto.randomUUID`). */
function generateMessageId(): string {
  nextMessageId += 1
  return `agent-msg-${nextMessageId}`
}

export interface UseAgentThreadResult {
  readonly messages: readonly AgentMessage[]
  /** Pushes a new user turn and runs the staged agent response for it,
   * appending to the existing thread (never replacing it) — this is how
   * both the composer submit AND a follow-up chip's "ask" round-trip work. */
  readonly ask: (text: string) => void
  /** True while the most recent agent turn is still "thinking" (its
   * `stepDone` hasn't reached `steps.length` yet) — disables the composer
   * and follow-up chips while a turn is in flight, mirroring
   * `MaterialsAiModal`'s `loading`-gated composer. */
  readonly busy: boolean
  /** Clears the thread back to the empty-prompt state ("New chat", UID-36
   * design-fidelity pass) — cancels any in-flight staged reveal first, since
   * a cleared thread has nothing left for a stale timer to update. */
  readonly reset: () => void
}

/**
 * Drives the sourcing-agent thread (UID-36, README §7 screen 2 / §13 Epic
 * C1): a plain client-side heuristic over REAL catalog-search results (never
 * a canned parts list — see `agentHeuristics.ts`'s doc comment), reproducing
 * the design source's staged "thinking" reveal (`askAgent`'s three staggered
 * `setTimeout`s at ~520/1080/1680ms).
 *
 * `service` mirrors `useCatalogSearch`'s injectable-service seam so tests
 * never hit the network; defaults to the real BFF-backed implementation.
 */
export function useAgentThread(
  service: Pick<CatalogSearchService, 'search'> = defaultCatalogSearchService,
): UseAgentThreadResult {
  const [messages, setMessages] = useState<readonly AgentMessage[]>([])
  const serviceRef = useRef(service)
  serviceRef.current = service
  const timeoutsRef = useRef<ReturnType<typeof setTimeout>[]>([])

  const clearPendingTimeouts = useCallback(() => {
    for (const timeout of timeoutsRef.current) clearTimeout(timeout)
    timeoutsRef.current = []
  }, [])

  // Clear any in-flight staged reveal on unmount — never let a stale
  // `setTimeout` fire a `setState` after this hook's owner has unmounted.
  useEffect(() => clearPendingTimeouts, [clearPendingTimeouts])

  const updateMessage = useCallback((id: string, patch: Partial<AgentAgentMessage>) => {
    setMessages((current) =>
      current.map((message) =>
        message.id === id && message.role === 'agent' ? { ...message, ...patch } : message,
      ),
    )
  }, [])

  const ask = useCallback(
    (text: string) => {
      const question = text.trim()
      if (!question) return

      // A prior turn's staged reveal is superseded by this new one — cancel
      // it (its message stays in the thread frozen at whatever `stepDone`
      // it had reached; only ONE turn is ever "in flight" at a time).
      clearPendingTimeouts()

      const agentMessageId = generateMessageId()
      setMessages((current) => [
        ...current,
        { id: generateMessageId(), role: 'user', text: question },
        {
          id: agentMessageId,
          role: 'agent',
          steps: AGENT_THINKING_STEPS,
          stepDone: 0,
          summary: null,
          shortlist: null,
          follows: [],
        },
      ])

      // UID-36 demo hardcoding: a mocked suggestion-chip prompt (see
      // `mockedAgentResults.ts`) describes an intent the real search can't
      // resolve, so it gets a canned shortlist instead of hitting the BFF.
      const mockedShortlist = getMockedAgentShortlist(question)
      const searchPromise = mockedShortlist
        ? Promise.resolve({
            items: mockedShortlist,
            nextCursor: null,
            totalCount: mockedShortlist.length,
          })
        : serviceRef.current
            .search(question, { limit: AGENT_SEARCH_LIMIT })
            .catch(() => ({ items: [], nextCursor: null, totalCount: 0 }))

      const [delay1, delay2, delay3] = AGENT_STEP_DELAYS_MS
      timeoutsRef.current.push(
        setTimeout(() => updateMessage(agentMessageId, { stepDone: 1 }), delay1),
        setTimeout(() => updateMessage(agentMessageId, { stepDone: 2 }), delay2),
        setTimeout(() => {
          void searchPromise.then((results) => {
            const shortlist = buildAgentShortlist(results.items)
            updateMessage(agentMessageId, {
              stepDone: 3,
              shortlist,
              summary: agentSummary(shortlist),
              follows: agentFollowUps(shortlist),
            })
          })
        }, delay3),
      )
    },
    [clearPendingTimeouts, updateMessage],
  )

  const reset = useCallback(() => {
    clearPendingTimeouts()
    setMessages([])
  }, [clearPendingTimeouts])

  const lastMessage = messages[messages.length - 1]
  const busy =
    lastMessage !== undefined &&
    lastMessage.role === 'agent' &&
    lastMessage.stepDone < lastMessage.steps.length

  return { messages, ask, busy, reset }
}
